import React from 'react';
import {Pressable, ScrollView, StyleSheet, View} from 'react-native';
import openMap from 'react-native-open-maps';
import call from 'react-native-phone-call';
import {faMapMarkerAlt} from '@fortawesome/free-solid-svg-icons/faMapMarkerAlt';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {observer} from 'mobx-react-lite';

import Button from '../components/ui-controls/Button';
import ContentView from '../components/ui-controls/layout/ContentView';
import Text from '../components/ui-controls/Text';
import {
  DAYS_OF_THE_WEEK,
  PUBLIC_HOLIDAYS,
  PublicHolidayNames,
} from '../constants/days';
import {AppRoute} from '../models/AppRoute';
import {RouteNavigatorScreenProps} from '../models/navigationTypes';
import {useStores} from '../stores/use-stores';
import theme from '../style/theme';
import {renderAddressLines} from '../utils/address';

const OpenHoursLine = ({
  day,
  openTime,
  closeTime,
  isClosed = false,
}: {
  day: string;
  openTime: string;
  closeTime: string;
  isClosed?: boolean;
}) => {
  return (
    <View
      style={[
        styles.fullWidthRow,
        {justifyContent: 'space-between', marginBottom: 8},
      ]}>
      <View style={{flex: 1, alignItems: 'flex-start'}}>
        <Text style={[{textTransform: 'capitalize'}, styles.addressText]}>
          {day}
        </Text>
      </View>
      <View
        style={{
          flex: 1,
          flexDirection: 'row',
          justifyContent: 'center',
        }}>
        {isClosed ? (
          <View
            style={{
              alignItems: 'center',
            }}>
            <Text style={[{textTransform: 'uppercase'}, styles.addressText]}>
              CLOSED
            </Text>
          </View>
        ) : (
          <>
            <View
              style={{
                flex: 1,
                alignItems: 'flex-start',
              }}>
              <Text
                style={[
                  {textTransform: 'uppercase', flex: 1},
                  styles.addressText,
                ]}>
                {openTime}
              </Text>
            </View>
            <View
              style={{
                alignItems: 'center',
              }}>
              <Text style={[{textTransform: 'uppercase'}, styles.addressText]}>
                -
              </Text>
            </View>
            <View
              style={{
                flex: 1,
                alignItems: 'flex-end',
              }}>
              <Text
                style={[
                  {textTransform: 'uppercase', flex: 1},
                  styles.addressText,
                ]}>
                {closeTime}
              </Text>
            </View>
          </>
        )}
      </View>
    </View>
  );
};

const CLOSED_TIME = '12:01 AM';

const OpenHoursTable = ({store}: {store: any}) => (
  <>
    <Text
      style={[
        styles.primary,
        styles.boldTitle,
        {marginTop: 16, marginBottom: 8},
      ]}>
      Normal Trading Hours:
    </Text>
    {DAYS_OF_THE_WEEK.map(day => {
      const openTime = store.regular_hours?.[day]?.from;
      const closeTime = store.regular_hours?.[day]?.until;
      let isClosed = false;

      // 😎
      if (
        closeTime === CLOSED_TIME ||
        openTime === CLOSED_TIME ||
        openTime === closeTime
      ) {
        isClosed = true;
      }

      if (!openTime || !closeTime) {
        return null;
      }

      return (
        <OpenHoursLine
          key={day}
          day={day}
          openTime={openTime}
          closeTime={closeTime}
          isClosed={isClosed}
        />
      );
    })}
  </>
);

const NoOpenHoursMessage = () => (
  <Text
    style={[
      styles.primary,
      styles.boldTitle,
      {marginTop: 16, marginBottom: 8, textAlign: 'center'},
    ]}
    testID="no-open-hours-message-text">
    Please contact this store for operating hours
  </Text>
);

const HolidaysHoursTable = ({store}: {store: any}) => (
  <>
    <Text
      style={[
        styles.primary,
        styles.boldTitle,
        {marginTop: 16, marginBottom: 8},
      ]}>
      Public Holiday Trading Hours:
    </Text>
    {PUBLIC_HOLIDAYS.map(publicHoliday => {
      const openTime = store.regular_hours?.[publicHoliday]?.from;
      const closeTime = store.regular_hours?.[publicHoliday]?.until;

      let isClosed = false;

      // 😎
      if (
        closeTime === CLOSED_TIME ||
        openTime === CLOSED_TIME ||
        openTime === closeTime
      ) {
        isClosed = true;
      }

      if (!openTime || !closeTime) {
        return null;
      }

      return (
        <OpenHoursLine
          key={publicHoliday}
          // @ts-ignore
          day={PublicHolidayNames[publicHoliday]}
          openTime={openTime}
          closeTime={closeTime}
          isClosed={isClosed}
        />
      );
    })}
  </>
);

const lineWithTrailingSpaceOrEmpty = (line: string) => (line ? line + ' ' : '');
const renderAddressSuburbLine = (
  locality: string,
  administrativeArea: string,
  postalCode: string,
) =>
  `${lineWithTrailingSpaceOrEmpty(locality)}${lineWithTrailingSpaceOrEmpty(
    administrativeArea,
  )}${lineWithTrailingSpaceOrEmpty(postalCode)}`;
const renderAddressText = (store: any) =>
  `${renderAddressLines(store.address?.lines)}\n${renderAddressSuburbLine(
    store.address?.locality,
    store.address?.administrative_area,
    store.address?.postal_code,
  )}`;

const BasicStoreDetails = ({store}: {store: any}) => (
  <View
    style={[
      {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignContent: 'center',
      },
    ]}>
    <View>
      <Text style={[styles.boldTitle, styles.primary, {marginBottom: 16}]}>
        {store.name}
      </Text>
      <Text style={[styles.addressText]}>{renderAddressText(store)}</Text>
    </View>
  </View>
);

const GetDirectionsButton = ({store}: {store: any}) => (
  <Button
    type="outline"
    containerStyle={styles.getDirectionsButton}
    onPress={() =>
      openMap({
        query: [
          store.name,
          store.address.lines.join(' '),
          store.address.locality,
        ].join(','),
      })
    }
    icon={
      <FontAwesomeIcon
        icon={faMapMarkerAlt}
        style={{
          color: theme.colour.uiControls.primary,
          marginRight: 8,
        }}
        size={12}
      />
    }
    title="GET DIRECTIONS"
  />
);

const ContactPhoneLine = ({store}: {store: any}) => {
  if (!store?.phone) return null;

  const phoneLinkOpts = store?.phone
    ? {
        number: store.phone,
        prompt: true,
      }
    : null;

  return (
    <View
      style={[
        styles.fullWidthRow,
        {
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 16,
        },
      ]}>
      <Text style={[styles.primary, styles.boldTitle]}>Contact:</Text>
      <Pressable onPress={() => call(phoneLinkOpts).catch(console.error)}>
        <Text style={[styles.primary, styles.addressText, {marginTop: 8}]}>
          {store.phone}
        </Text>
      </Pressable>
    </View>
  );
};

const Notes = ({store}: {store: any}) =>
  store.regular_hours?.notes ? (
    <Text style={styles.notes}>*{store.regular_hours.notes}</Text>
  ) : null;

const LiquorLicense = ({license}: {license: string | null}) => (
  <View style={{flexDirection: 'row', marginTop: 16}}>
    <Text style={[styles.addressText, styles.marginRight]}>
      Liquor License:
    </Text>
    <Text style={[styles.addressText, styles.marginRight]}>{license}</Text>
  </View>
);

export default observer(
  ({navigation}: RouteNavigatorScreenProps<AppRoute.MY_STORE>) => {
    const {shopSelectionStore} = useStores();
    const store = shopSelectionStore.selectedStore;

    const ChangeStoreButton = () => (
      <Button
        containerStyle={styles.changeStoreButton}
        // TODO: This should go to the store selection screen if suburbs
        // and stores are loaded, not to the initial suburb selection search
        onPress={() => navigation.navigate(AppRoute.MY_STORE_SUBURB_SELECTION)}
        title="CHANGE STORE"
      />
    );

    if (!store) {
      return null;
    }

    const hasOpenAndCloseHours = (day: string) =>
      store.regular_hours?.[day]?.from && store.regular_hours?.[day]?.until;
    const everyDayHasHours = DAYS_OF_THE_WEEK.every(hasOpenAndCloseHours);
    const everyPublicHolidayHasHours =
      PUBLIC_HOLIDAYS.every(hasOpenAndCloseHours);

    return (
      <ContentView>
        <ScrollView
          showsVerticalScrollIndicator={true}
          showsHorizontalScrollIndicator={false}>
          <ChangeStoreButton />
          <View style={styles.flexTop}>
            <View style={styles.detailsMain}>
              <BasicStoreDetails store={store} />
              <GetDirectionsButton store={store} />
              <ContactPhoneLine store={store} />
              {everyDayHasHours ? (
                <>
                  <OpenHoursTable store={store} />
                  {!!everyPublicHolidayHasHours && (
                    <HolidaysHoursTable store={store} />
                  )}
                </>
              ) : (
                <NoOpenHoursMessage />
              )}
              {false}
              <Notes store={store} />
              <LiquorLicense license={shopSelectionStore.liquorLicense} />
            </View>
          </View>
        </ScrollView>
      </ContentView>
    );
  },
);

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
    justifyContent: 'space-between',
  },
  detailsMain: {
    flex: 1,
    width: '100%',
  },
  primary: {
    color: theme.colour.text.heading.primary,
  },
  marginRight: {
    marginRight: 4,
  },
  boldTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  addressText: {
    fontSize: 16,
    fontWeight: '400',
  },
  fullWidthRow: {
    width: '100%',
    flexDirection: 'row',
  },
  changeStoreButton: {
    marginTop: 8,
    marginBottom: 16,
  },
  getDirectionsButton: {
    marginTop: 16,
  },
  getDirectionsText: {
    color: theme.colour.text.heading.primary,
    fontSize: 16,
    paddingHorizontal: 8,
    paddingVertical: 8,
  },
  changeStoreText: {
    color: 'white',
    fontSize: 16,
    paddingHorizontal: 8,
    paddingVertical: 8,
  },
  flexTop: {
    flex: 1,
  },
  notes: {
    marginTop: 8,
    fontStyle: 'italic',
  },
});
